import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  resolvePkAtPosition,
  PkCandidate,
} from '../utils/resolvePkAtPosition';
import { getPositionSource, isNativeApp } from '../utils/positionSource';
import { traceAutolocate } from '../utils/autolocateTrace';
import { showSuccessToast, showErrorToast } from '../utils/toasts';
import { pkFormatter } from '../utils/transform';
import { RoisBackgroundLocation } from '../plugins/roisBackgroundLocation';

/** Qué hace el formulario con la vía y el PK que salen del GPS. */
export type AutolocateApply = (match: PkCandidate) => void;

/**
 * Autolocalizar PK: posición actual → vía y PK → formulario.
 *
 * ── El permiso, que es lo que más se tuerce ────────────────────────────────
 *
 * 🔴 NO se consulta `navigator.permissions` para decidir. Dentro del WebView
 * esa respuesta no decide nada —Capacitor concede el origen sin retenerlo, así
 * que lo que devuelve es un valor por defecto, no la decisión de nadie— y
 * cortar por ella impide que salga el diálogo de Android. Es lo que dejó muda
 * a la cámara en el APK y lo que encerró al operario en el modal de «GPS
 * requerido» con un botón que no hacía nada.
 *
 * Se llama y se atiende el fallo: `code === 1` es la negativa REAL. Y solo
 * entonces, y solo en la app, se ofrecen los ajustes — desde Android 11 un
 * permiso rechazado dos veces ya no vuelve a preguntar, así que sin eso el
 * operario se queda sin salida.
 *
 * ── Por qué el formulario pone los datos y no este hook ────────────────────
 *
 * Los dos consumidores nombran sus campos distinto (`pkInit` en el asistente
 * de creación, `actionPkInit` en el de validación) y uno de ellos tiene además
 * un desplegable con ref imperativa. `onApply` deja esa parte donde vive el
 * formulario; aquí solo se decide QUÉ se ha encontrado y qué se le cuenta al
 * operario.
 */
export function useAutolocatePk(args: {
  getPreferredRoadId: () => string | null | undefined;
  getConcessionId: () => string | null | undefined;
  onApply: AutolocateApply;
}): {
  /**
   * @param posicion posición YA obtenida, si el llamador acaba de pedirla. La
   * pestaña Ubicación la tiene porque el mismo botón centra el mapa: sin esto
   * se pedirían DOS lecturas de GPS por pulsación —más batería, más espera— y,
   * peor, el marcador y el PK saldrían de fixes distintos, así que podrían no
   * corresponderse.
   */
  run: (posicion?: GeolocationPosition) => Promise<void>;
  isLoading: boolean;
} {
  const client = useApolloClient();
  const { t } = useTranslation('forms');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Guard contra la doble pulsación.
   *
   * `isLoading` deshabilita el botón, pero eso ocurre en el render SIGUIENTE:
   * dos toques dentro del mismo fotograma entran los dos. En una tablet con
   * guantes eso no es raro, es lo normal — el operario ya reportó un caso de
   * «al pulsar parecía que pulsaba más».
   */
  const enCurso = useRef(false);

  // Los callbacks del formulario cambian en cada render; guardarlos en una ref
  // mantiene `run` estable y evita que el botón se re-cree en cada pulsación.
  const argsRef = useRef(args);
  argsRef.current = args;

  const run = useCallback(
    async (posicionDada?: GeolocationPosition) => {
    if (enCurso.current) return;
    enCurso.current = true;
    setIsLoading(true);
    try {
      const posicion = posicionDada ?? (await posicionActual(t));
      if (!posicion) return;

      // 🔴 `accuracy` es el dato que decide si un «no estás sobre ninguna vía»
      // habla del operario o del aparato. Sin él en campo, las dos causas
      // posibles —fix de antena o malla incompleta— son indistinguibles.
      traceAutolocate('position', {
        lat: posicion.coords.latitude,
        lng: posicion.coords.longitude,
        accuracy: posicion.coords.accuracy,
        ageMs: Date.now() - posicion.timestamp,
        dada: posicionDada !== undefined,
      });

      const { getPreferredRoadId, getConcessionId, onApply } = argsRef.current;
      const resolucion = await resolvePkAtPosition(client, {
        lat: posicion.coords.latitude,
        lng: posicion.coords.longitude,
        preferredRoadId: getPreferredRoadId(),
        concessionId: getConcessionId(),
      });

      const pintaPk = (c: PkCandidate) => pkFormatter(c.pkKm, c.pkMeter, true);

      traceAutolocate('resolution', {
        kind: resolucion.kind,
        ...(resolucion.kind === 'ok'
          ? {
              road: resolucion.match.roadName,
              meters: Math.round(resolucion.match.distance),
            }
          : {}),
        ...(resolucion.kind === 'too-far'
          ? {
              road: resolucion.nearest.roadName,
              meters: Math.round(resolucion.nearest.distance),
            }
          : {}),
      });

      switch (resolucion.kind) {
        case 'ok': {
          onApply(resolucion.match);
          const pk = pintaPk(resolucion.match);
          const road = resolucion.match.roadName;
          if (resolucion.ambiguousWith) {
            showSuccessToast(
              t('workOrder.autolocate.ambiguous', {
                pk,
                road,
                other: resolucion.ambiguousWith.roadName,
              })
            );
          } else if (resolucion.changedRoad) {
            showSuccessToast(t('workOrder.autolocate.roadChanged', { pk, road }));
          } else {
            showSuccessToast(t('workOrder.autolocate.filled', { pk, road }));
          }
          return;
        }
        case 'too-far':
          showErrorToast(
            t('workOrder.autolocate.tooFar', {
              road: resolucion.nearest.roadName,
              // Redondeado: «a 480,4 m» es ruido; «a 480 m» es información.
              meters: Math.round(resolucion.nearest.distance),
            })
          );
          return;
        case 'out-of-range': {
          const r = resolucion.match.roadRange;
          showErrorToast(
            t('workOrder.autolocate.outOfRange', {
              pk: pintaPk(resolucion.match),
              road: resolucion.match.roadName,
              range: r
                ? `${pkFormatter(r.pkInitKm, r.pkInitMeter, true)} — ${pkFormatter(
                    r.pkEndKm,
                    r.pkEndMeter,
                    true
                  )}`
                : '',
            })
          );
          return;
        }
        case 'no-data':
          showErrorToast(t('workOrder.autolocate.noMesh'));
          return;
      }
    } finally {
      enCurso.current = false;
      setIsLoading(false);
    }
    },
    [client, t]
  );

  return { run, isLoading };
}

/**
 * La posición actual, o `null` si no se ha podido y ya se ha avisado.
 *
 * Se saca del hook porque no necesita nada de React y así el `switch` de
 * arriba se lee de un vistazo.
 */
async function posicionActual(
  t: (clave: string) => string
): Promise<GeolocationPosition | null> {
  const source = getPositionSource();
  if (!source) {
    showErrorToast(t('workOrder.autolocate.unavailable'));
    return null;
  }

  try {
    return await new Promise<GeolocationPosition>((resolve, reject) => {
      source.getCurrent(resolve, reject);
    });
  } catch (err) {
    const code = (err as { code?: number } | undefined)?.code;
    // `code === 1` es PERMISSION_DENIED, la única negativa real del usuario.
    const denegado = code === 1;
    showErrorToast(
      t(
        denegado
          ? 'workOrder.autolocate.denied'
          : 'workOrder.autolocate.unavailable'
      )
    );
    if (denegado && isNativeApp()) {
      await RoisBackgroundLocation.openAppSettings().catch(() => undefined);
    }
    return null;
  }
}

export default useAutolocatePk;
